import React, { useCallback, useMemo } from 'react';
import {
  arrayOf, func, bool, string, any, objectOf, oneOf, shape,
} from 'prop-types';
import { Transcript as TranscriptIcon } from '@picsio/icons';
import { IconButton } from '@picsio/ui';
import { navigate, isRoutePreview } from '../../../../helpers/history';
import Logger from '../../../../services/Logger';
import picsioConfig from '../../../../../../../config';
import { calculateAiMinutes } from '../../../../helpers/calculateAICredits';
import { parseTime } from '../../../../shared/utils';
import DetailsBlock from '../../../DetailsBlock';
import l18n from '../../../../shared/strings';
import { showDialog } from '../../../dialog';
import Toast from '../../../Toast/index';
import Placeholder from '../../../DetailsBlock/Placeholder';
import ProcessingItem from '../ProcessingItem';
import AiIcon from '../AiIcon';
import Tooltip from '../../../Tooltip';
import './styles.scss';

function Transcript({
  assetIds,
  assets,
  team,
  detailsPanelVisibility,
  toggleVisibility,
  blockTitle,
  blockName,
  isMainApp,
  transcribeAction,
  transcriptPermission,
  subscriptionFeatures,
  transcribing,
}) {
  const defaultLanguage = team?.policies?.transcribingLanguage || 'en';

  const isMediaAsset = assets.length === 1 && (assets[0]?.isVideo || assets[0]?.isAudio);

  const canTranscribe = assetIds.length >= 2 || isMediaAsset;

  const transcriptPreview = useMemo(() => {
    /** if selected more than one asset or on the asset no transcripts */
    if (assets.length !== 1 || !assets[0]?.transcripts?.length) return null;
    const asset = assets[0];
    const { transcripts, headRevisionId } = asset;
    const t = transcripts.find(({ revisionId }) => revisionId === headRevisionId) || null;

    if (!t) return null;
    const { transcript } = t;
    if (transcript?.length) {
      const fullPreview = transcript.map(({ text }) => text || '').join(' ');
      return fullPreview.length > 150 ? `${fullPreview.slice(0, 147)} ...` : fullPreview;
    }
    return null;
  }, [assets]);

  const showTranscriptPreview = useCallback((e) => {
    e.preventDefault();
    if (picsioConfig.isMainApp) {
      if (isRoutePreview()) window.dispatchEvent(new Event('hotkeys:preview:showTranscript'));
      else navigate(`/preview/${assetIds[0]}#transcription`);
    }
  }, [assetIds]);

  const handleSubmit = useCallback(() => {
    if (isMainApp && assetIds.length) {
      const locale = defaultLanguage;
      Logger.log('User', 'InfoPanelTranscribeClicked', { assetsLength: assetIds.length, language: locale });
      const toProcess = () => {
        if (assetIds.length >= 2) {
          showDialog({
            title: l18n.TRANSCRIPT.DIALOGS.titleBulkTranscribing,
            text: l18n.TRANSCRIPT.DIALOGS.textBulkTranscribing(assetIds.length),
            textBtnCancel: l18n.TRANSCRIPT.DIALOGS.btnCancelBulkTranscribing,
            textBtnOk: l18n.TRANSCRIPT.DIALOGS.btnCongirmBulkTranscribing,
            onOk: () => {
              transcribeAction({ assetIds, language: locale });
            },
          });
        } else {
          transcribeAction({ assetIds, language: locale });
        }
      };
      if (transcriptPreview) {
        showDialog({
          title: l18n.TRANSCRIPT.DIALOGS.titleExistingTranscript,
          text: l18n.TRANSCRIPT.DIALOGS.textExistingTranscript(),
          textBtnCancel: l18n.TRANSCRIPT.DIALOGS.btnCancelBulkTranscribing,
          textBtnOk: l18n.TRANSCRIPT.DIALOGS.btnCongirmBulkTranscribing,
          onOk: () => {
            Logger.log('User', 'OverwriteTranscriptConfirmed');
            toProcess();
          },
          onCancel: () => Logger.log('User', 'OverwriteTranscriptModalClosed'),
        });
        Logger.log('User', 'OverwriteTranscriptModalShown');
      } else {
        toProcess();
      }
    } else {
      Toast(l18n.TRANSCRIPT.TOASTS.textTranscribingHonSupported);
    }
  }, [isMainApp, assetIds, defaultLanguage, transcribeAction, transcriptPreview]);

  const inProgress = useMemo(
    () => ['waiting', 'running'].includes(transcribing),
    [transcribing],
  );

  const aiMinutes = useMemo(() => calculateAiMinutes(team), [team]);
  const aiMinutesString = parseTime(aiMinutes * 60, { type: 'hoursAndMinutes' });

  const actions = useMemo(
    () => {
      if (!transcriptPermission) return [];

      const result = [];
      if (assetIds.length === 1) {
        result.push(
          <Tooltip key="open-panel" content="Open transcript panel" placement="top">
            <IconButton
              onClick={showTranscriptPreview}
              componentProps={{ 'data-testid': 'openTranscriptPanel' }}
              size="lg"
            >
              <TranscriptIcon />
            </IconButton>
          </Tooltip>,
        );
      }
      result.push(
        <AiIcon
          key="ai-icon"
          data-testid="icongenerateTranscribe"
          size="lg"
          inProgress={inProgress}
          tooltipText={!subscriptionFeatures || !aiMinutes ? l18n.DETAILS.aiIconUpdatePlanTooltipText('Convert audio to text.') : l18n.DETAILS.aiIconMinutesTooltipText(inProgress ? l18n.DETAILS.textTranscribing : l18n.DETAILS.textTranscribe, aiMinutesString, 'text-center')}
          onSubmit={handleSubmit}
        />,
      );
      return result;
    },
    [assetIds, aiMinutes, aiMinutesString, handleSubmit, inProgress, showTranscriptPreview, subscriptionFeatures, transcriptPermission],
  );

  /** Don't show block if selected only one asset AND it's not a videos or audio asset */
  if (!canTranscribe) return null;

  return (
    <DetailsBlock
      showAi={false}
      dataQa="transcript"
      detailsPanelVisibility={detailsPanelVisibility}
      toggleVisibility={toggleVisibility}
      blockName={blockName}
      blockTitle={blockTitle}
      showUpgradePlan={!subscriptionFeatures}
      badgeType="minutes"
      actions={actions}
    >
      <If condition={!transcriptPreview}>
        <Placeholder>
          <Choose>
            <When condition={inProgress}>
              <ProcessingItem item={{
                status: 'running',
                name: 'transcribing',
                icon: <TranscriptIcon />,
              }}
              />
            </When>
            <When condition={assetIds.length > 1}>
              {l18n.DETAILS.placeholderMultipleSelection}
            </When>
            <Otherwise>
              {l18n.DETAILS.placeholderTranscribe}
            </Otherwise>
          </Choose>
        </Placeholder>
      </If>
      <If condition={transcriptPreview}>
        <Tooltip content="Click to read full transcript" placement="top">
          <div role="presentation" onClick={showTranscriptPreview} className="transcript-preview">
            {transcriptPreview}
          </div>
        </Tooltip>
      </If>
    </DetailsBlock>
  );
}

Transcript.defaultProps = {
  detailsPanelVisibility: {},
  isMainApp: true,
  transcriptPermission: false,
  transcribeAction: Function.prototype,
  subscriptionFeatures: false,
  transcribing: 'skipped',
  assets: [],
};

Transcript.propTypes = {
  assetIds: arrayOf(string).isRequired,
  team: objectOf(any).isRequired,
  detailsPanelVisibility: objectOf(any),
  toggleVisibility: func.isRequired,
  blockTitle: string.isRequired,
  blockName: string.isRequired,
  isMainApp: bool,
  transcriptPermission: bool,
  transcribeAction: func,
  subscriptionFeatures: bool,
  transcribing: oneOf(['skipped', 'waiting', 'running', 'complete', 'failed', 'rejected']),
  assets: arrayOf(shape({
    headRevisionId: string,
    transcripts: arrayOf(shape({
      language: string,
      revisionId: string,
      transcript: shape({
        start: string,
        end: string,
        text: string,
      }),
    })),
  })),
};

export default Transcript;
